import {Link} from 'react-router-dom';
import {Theme} from '../../../components/atoms/theme';

// Renders audit-log rows as humans read them: entity names instead of
// UUIDs, and change summaries built from metadata instead of raw messages.

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const shortId = (id?: string | null) => (id ? `${String(id).slice(0, 8)}…` : '—');

const ENTITY_SINGULAR: Record<string, string> = {
  REQUEST: 'Request',
  MEDICAL_REQUEST: 'Medical request',
  FIRM: 'Law firm',
  PATIENT: 'Patient',
  PROVIDER: 'Provider',
  USER: 'User',
  PROVIDER_CONNECTION: 'Provider connection',
  PROVIDER_NOTE: 'Provider note',
  REQUEST_NOTE: 'Request note',
  PATIENT_FORM: 'Patient form',
};

interface RecordInfo {
  typeLabel: string;
  name: string;
  sub?: string;
  to?: string;
}

export function recordInfo(log: any): RecordInfo {
  const typeLabel = ENTITY_SINGULAR[log.entityType] || log.entityType || 'Record';
  switch (log.entityType) {
    case 'REQUEST':
      return {
        typeLabel,
        name: log.patientName || shortId(log.entityId),
        sub: log.providerName || undefined,
        to: `/admin/requests/${log.entityId}`,
      };
    case 'MEDICAL_REQUEST':
      return {
        typeLabel,
        name: log.patientName || shortId(log.requestId || log.entityId),
        sub: log.providerName || undefined,
        to: log.requestId ? `/admin/requests/${log.requestId}` : undefined,
      };
    case 'PATIENT':
      return {
        typeLabel,
        name: log.patientName || shortId(log.entityId),
        to: `/admin/patients/${log.entityId}`,
      };
    case 'PROVIDER':
      return {
        typeLabel,
        name: log.providerName || shortId(log.entityId),
        to: `/admin/medical-providers/${log.entityId}`,
      };
    case 'FIRM':
      return {
        typeLabel,
        name: log.firmName || shortId(log.entityId),
        to: `/admin/law-firms/${log.entityId}`,
      };
    default: {
      const name = log.patientName || log.providerName || log.firmName || shortId(log.entityId);
      return {
        typeLabel,
        name,
        to: log.requestId ? `/admin/requests/${log.requestId}` : undefined,
      };
    }
  }
}

export const RecordCell = ({log}: {log: any}) => {
  const info = recordInfo(log);
  const body = (
    <>
      <span className="mr-2 text-[10px] font-bold uppercase tracking-wide text-gray-400">{info.typeLabel}</span>
      <span className="text-xs font-semibold text-gray-800">{info.name}</span>
      {info.sub && <span className="ml-1.5 text-xs text-gray-400">· {info.sub}</span>}
    </>
  );
  return info.to ? (
    <Link to={info.to} onClick={(e) => e.stopPropagation()} className="hover:underline" style={{color: 'inherit', textDecoration: 'none'}}>
      {body}
    </Link>
  ) : (
    <span>{body}</span>
  );
};

const Pill = ({children, accent}: {children: any; accent?: boolean}) => (
  <span
    className="inline-block rounded-md border px-1.5 py-px font-mono text-[11px]"
    style={
      accent
        ? {
            background: '#F7E9FB',
            borderColor: '#EDD3F6',
            color: Theme.colors.primaries_000,
          }
        : {background: '#F1EDF6', borderColor: '#E5DFEC', color: '#201A2C'}
    }
  >
    {children}
  </span>
);

const Tag = ({children, color}: {children: any; color: string}) => (
  <span className="rounded-md px-1.5 py-px text-[10.5px] font-bold uppercase tracking-wide" style={{color, background: `${color}20`}}>
    {children}
  </span>
);

const prettifyField = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const NOISE_FIELDS = new Set(['updatedAt', 'createdAt', 'seeded']);

export const ChangeCell = ({log}: {log: any}) => {
  const md = log.metadata || {};
  const oldValue = md.oldValue ?? md.oldStatus;
  const newValue = md.newValue ?? md.newStatus;

  switch (log.eventType) {
    case 'STATUS_CHANGE':
      if (oldValue || newValue) {
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Pill>{String(oldValue ?? '—')}</Pill>
            <span className="text-gray-400">→</span>
            <Pill accent>{String(newValue ?? '—')}</Pill>
          </span>
        );
      }
      break;
    case 'CREATE':
    case 'MEDICAL_REQUEST_CREATE':
      return <Tag color="#0CA678">Created</Tag>;
    case 'DELETE':
    case 'MEDICAL_REQUEST_DELETE':
      return <Tag color="#E64980">Deleted</Tag>;
    case 'ATTACHMENT_ADDED':
      return (
        <span className="inline-flex items-center gap-1.5">
          <Tag color="#F08C00">Attachment</Tag>
          {md.fileName && <Pill>{md.fileName}</Pill>}
        </span>
      );
    case 'ATTACHMENT_REMOVED':
      return (
        <span className="inline-flex items-center gap-1.5">
          <Tag color="#E64980">Removed</Tag>
          {md.fileName && <Pill>{md.fileName}</Pill>}
        </span>
      );
    case 'MARK_AS_SENT':
      return <Tag color="#AE3EC9">Marked as sent</Tag>;
    case 'UPLOAD_TO_FILEVINE':
      return <Tag color="#F08C00">Uploaded to FileVine</Tag>;
    case 'PROVIDER_VERIFIED':
      return <Tag color="#0CA678">Verified</Tag>;
    case 'BULK_UPDATE':
      return (
        <span className="text-xs text-gray-600">
          Bulk updated <b>{md.count ? `${md.count} requests` : 'multiple requests'}</b>
        </span>
      );
    case 'UPDATE':
    case 'MEDICAL_REQUEST_UPDATE': {
      const fields = Object.keys(md.changes || {}).filter((f) => !NOISE_FIELDS.has(f));
      if (fields.length) {
        const shown = fields.slice(0, 3).map(prettifyField).join(', ');
        return (
          <span className="text-xs text-gray-600">
            Updated <b>{shown}</b>
            {fields.length > 3 && ` +${fields.length - 3} more`}
          </span>
        );
      }
      if (md.created) return <Tag color="#0CA678">Created</Tag>;
      break;
    }
    default:
      break;
  }

  // Fallback: the raw message, minus the "User <name> " prefix and with
  // UUIDs shortened so it stays readable.
  const msg = String(log.message || '')
    .replace(new RegExp(`^User ${log.userName} `), '')
    .replace(UUID_RE, (m) => `${m.slice(0, 8)}…`);
  return <span className="text-xs text-gray-600">{msg.charAt(0).toUpperCase() + msg.slice(1)}</span>;
};
